import React, { useState, useEffect, useRef } from 'react'
import { CODE_LOG_CONFIG, LANGUAGE_MAP } from '../utils/constants'
import { detectLanguageFromPaste, getLanguageInfo } from '../utils/fileHelpers'
import CodeFilePad from './CodeFilePad'
import Pressable from './Pressable'

/* ───────────────────────────────────────────────────────────────
 *  CODE MODAL
 *
 *  Two entry points, ONE sheet design (tall ~88%, phone-width):
 *
 *  CREATE  (from + log bar → Code):
 *    - Sheet shows Title + grouped Files list + Add pill
 *    - Top row: X (discard) left, checkmark (send to feed) right
 *    - Tap file row → CodeFilePad
 *    - Pad's pencil-square = add a file + pop this sheet up OVER the
 *      pad (pad stays behind, chevron reachable above the sheet)
 *
 *  EDIT  (tap a code card in Home or Library):
 *    - File 0's pad opens in the BACKGROUND
 *    - The same sheet pops up in FRONT — but NO X / NO checkmark
 *    - Pick a file → enter its pad. Tap the pad's chevron (visible
 *      above the sheet) to quit the whole card. Changes autosave.
 *
 *  The pad always renders BEHIND the sheet whenever a file is open.
 * ─────────────────────────────────────────────────────────────── */
const MODAL_CONFIG = {
  overlayBg: 'rgba(0,0,0,0.55)',

  sheetBg: '#0f0f10',
  sheetRadius: '28px',
  sheetWidth: '430px',
  sheetHeightVh: 88,            // uses dvh at render-time for mobile browser chrome
  sheetPaddingX: '20px',
  sheetPaddingTop: '12px',
  sheetPaddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',

  handleColor: 'rgba(255,255,255,0.22)',
  handleWidth: '36px',
  handleHeight: '5px',
  handleMarginBottom: '14px',

  topBtnSize: 36,
  cancelBtnBg: 'rgba(120,120,128,0.36)',
  confirmBtnBg: '#2f93ff',
  topRowMarginBottom: '20px',

  sectionLabelColor: '#2f93ff',
  sectionLabelFontSize: '13px',
  sectionLabelFontWeight: 400,
  sectionLabelMarginBottom: '8px',
  sectionMarginBottom: '20px',

  // Grouped file/title card
  groupBg: 'rgba(255,255,255,0.06)',
  groupRadius: '18px',
  rowPaddingX: '18px',
  rowPaddingY: '20px',
  rowFontSize: '17px',
  rowFontWeight: 400,
  rowColor: '#fff',
  dividerInset: '18px',
  dividerColor: 'rgba(255,255,255,0.08)',

  titleInputFontSize: '17px',

  addBtnBg: '#2f93ff',
  addBtnRadius: '100px',
  addBtnHeight: '52px',
  addBtnFontSize: '17px',
  addBtnFontWeight: 400,
}

const makeEmptyFile = (idx) => ({
  name: `${CODE_LOG_CONFIG.defaultFileNamePrefix} ${idx + 1}`,
  language: CODE_LOG_CONFIG.defaultLanguage,
  content: '',
})

/* Most-used language across files (for feed strip color). */
export const primaryLanguageOf = (files) => {
  if (!files?.length) return null
  const counts = {}
  for (const f of files) {
    if (!f.language) continue
    counts[f.language] = (counts[f.language] || 0) + 1
  }
  let best = null
  let bestCount = 0
  for (const [lang, c] of Object.entries(counts)) {
    if (c > bestCount) { best = lang; bestCount = c }
  }
  return best
}

/* ── Icons (Oliver's exact paths) ── */
const XMark = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 10 10" fill="none">
    <path d="M9.19336 8.45508C9.39258 8.6543 9.39258 8.98828 9.19336 9.19336C8.99414 9.39844 8.64844 9.39258 8.44922 9.19336L4.69922 5.44336L0.949219 9.19336C0.75 9.39258 0.410156 9.39258 0.205078 9.19336C0 8.99414 0.00585938 8.6543 0.205078 8.45508L3.95508 4.69922L0.205078 0.949219C0.00585938 0.75 0.00585938 0.416016 0.205078 0.210938C0.404297 0 0.75 0.00585938 0.949219 0.210938L4.69922 3.96094L8.44922 0.210938C8.6543 0 8.98828 0.00585938 9.19336 0.210938C9.39844 0.410156 9.39258 0.75 9.19336 0.949219L5.44336 4.69922L9.19336 8.45508Z" fill="white"/>
  </svg>
)
const CheckMark = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 11 11" fill="none">
    <path d="M3.82031 10.0137C3.56836 10.0137 3.38672 9.90234 3.21094 9.67383L0.169922 5.80078C0.0585938 5.66016 0 5.50781 0 5.37305C0 5.05078 0.228516 4.82227 0.5625 4.82227C0.773438 4.82227 0.919922 4.9043 1.06641 5.10938L3.79688 8.68359L9.05859 0.298828C9.19922 0.0761719 9.32812 0 9.5625 0C9.88477 0 10.0957 0.210938 10.0957 0.533203C10.0957 0.667969 10.0547 0.796875 9.94922 0.966797L4.41211 9.68555C4.27148 9.90234 4.07227 10.0137 3.82031 10.0137Z" fill="white"/>
  </svg>
)

/* ───────────────────────────────────────────────────────────────
 *  CodeModal
 *
 *  Props (unchanged contract with App.js):
 *    show         : boolean
 *    onClose      : () => void
 *    onSubmit     : (folder) => void   — CONFIRM in create mode
 *    initialData  : null | { title, files, is_starred, id? }
 *                   present → EDIT mode (tap card to re-open)
 *    onToggleStar : () => void
 *    onDelete     : () => void
 *    isStarred    : boolean
 * ─────────────────────────────────────────────────────────────── */
function CodeModal({ show, onClose, onSubmit, initialData, onToggleStar, onDelete, isStarred }) {
  const isEdit = !!initialData

  const [title, setTitle] = useState('')
  const [files, setFiles] = useState([])
  const [openFileIdx, setOpenFileIdx] = useState(null)   // which pad is open (null = none)
  const [sheetVisible, setSheetVisible] = useState(true) // is the bottom sheet showing
  const [error, setError] = useState('')
  const browseInputRef = useRef(null)
  const browseTargetIdx = useRef(null)

  /* Seed on open.
   *  CREATE → sheet visible, no pad.
   *  EDIT   → file 0's pad open in back + sheet visible in front.
   *  We key the seed on show + the log id (not the initialData object
   *  identity, which changes every render) so star/silent-save updates
   *  don't wipe the user's in-progress edits. */
  const seedKey = isEdit ? (initialData.id ?? 'edit') : 'create'
  const seededRef = useRef(null)
  useEffect(() => {
    if (!show) { seededRef.current = null; return }
    if (seededRef.current === seedKey) return   // already seeded this session
    seededRef.current = seedKey
    if (isEdit) {
      setTitle(initialData.title || '')
      setFiles(initialData.files?.length ? initialData.files : [makeEmptyFile(0)])
      setOpenFileIdx(0)
      setSheetVisible(true)
    } else {
      setTitle('')
      setFiles([makeEmptyFile(0)])
      setOpenFileIdx(null)
      setSheetVisible(true)
    }
    setError('')
  }, [show, seedKey, isEdit])

  if (!show) return null

  const updateFileAt = (idx, next) => {
    setFiles(prev => prev.map((f, i) => i === idx ? next : f))
  }

  /* Add one empty file row. In EDIT mode we also autosave so the new
   * file persists even if the user just taps chevron to leave. */
  const handleAddFileRow = () => {
    if (files.length >= CODE_LOG_CONFIG.maxFilesPerFolder) {
      setError(`Max ${CODE_LOG_CONFIG.maxFilesPerFolder} files per folder`)
      return
    }
    setFiles(prev => [...prev, makeEmptyFile(prev.length)])
    setError('')
  }

  /* Pad's pencil-square: add a file AND pop the sheet up over the pad. */
  const handlePencilFromPad = () => {
    handleAddFileRow()
    setSheetVisible(true)
  }

  const handleDeleteFile = (idx) => {
    // Last remaining file → delete the whole code log.
    if (files.length === 1) {
      if (onDelete) onDelete()
      else onClose()
      return
    }
    // Otherwise just remove THIS file and stay in the sheet.
    const nextFiles = files.filter((_, i) => i !== idx)
    setFiles(nextFiles)
    setOpenFileIdx(null)
    setSheetVisible(true)
    // In edit mode, persist the removal immediately.
    if (isEdit) onSubmitSilent(nextFiles)
  }

  /* Save without closing (used by single-file delete in edit mode). */
  const onSubmitSilent = (nextFiles) => {
    if (!isEdit) return
    onSubmit({ title: (title || '').trim() || 'Code', files: nextFiles, _silent: true })
  }

  /* ── Browse / Copy ── */
  const handleBrowse = (targetIdx) => {
    browseTargetIdx.current = targetIdx
    browseInputRef.current?.click()
  }
  const handleBrowseSelected = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > CODE_LOG_CONFIG.maxFileSizeBytes) {
      alert(`File too large (max ${Math.round(CODE_LOG_CONFIG.maxFileSizeBytes / 1024 / 1024)}MB)`)
      return
    }
    try {
      const text = await file.text()
      const idx = browseTargetIdx.current
      if (idx == null) return
      const detected = detectLanguageFromPaste({ filename: file.name, content: text })
      setFiles(prev => prev.map((f, i) => i === idx
        ? { ...f, name: file.name.replace(/\.[^.]+$/, '') || f.name, content: text, language: detected || f.language }
        : f
      ))
    } catch (err) {
      alert('Could not read file: ' + err.message)
    }
  }
  const handleCopy = (idx) => {
    const f = files[idx]
    if (!f?.content) return
    try {
      navigator.clipboard.writeText(f.content)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = f.content
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch {}
      document.body.removeChild(ta)
    }
  }

  /* ── CREATE: checkmark confirm / X discard ── */
  const handleConfirm = () => {
    if (!title.trim()) {
      setError('Folder needs a title')
      return
    }
    const trimmed = files.filter((f, i) => {
      if (i === files.length - 1 && !f.content && /^File \d+$/.test(f.name || '')) return false
      return true
    })
    onSubmit({
      title: title.trim(),
      files: trimmed.length ? trimmed : [makeEmptyFile(0)],
    })
  }

  /* ── EDIT: save happens once, on chevron-quit (not per keystroke,
   *    which would close the modal mid-edit). We keep edits in local
   *    state and flush them via onSubmit when the user leaves. ── */
  const saveAndClose = () => {
    if (isEdit) {
      // Hide both layers immediately so there's no flash of the
      // pad+sheet re-rendering while the parent closes the modal.
      setOpenFileIdx(null)
      setSheetVisible(false)
      onSubmit({ title: (title || '').trim() || 'Code', files })
    } else {
      onClose()
    }
  }

  /* ── Styles ── */
  const topBtnStyle = (bg) => ({
    width: MODAL_CONFIG.topBtnSize + 'px',
    height: MODAL_CONFIG.topBtnSize + 'px',
    borderRadius: '50%',
    background: bg,
    backdropFilter: 'saturate(180%) blur(14px)',
    WebkitBackdropFilter: 'saturate(180%) blur(14px)',
    border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', padding: 0, flexShrink: 0,
  })
  const addPillStyle = {
    width: '100%',
    background: MODAL_CONFIG.addBtnBg,
    color: '#fff', border: 'none',
    borderRadius: MODAL_CONFIG.addBtnRadius,
    height: MODAL_CONFIG.addBtnHeight,
    fontSize: MODAL_CONFIG.addBtnFontSize,
    fontWeight: MODAL_CONFIG.addBtnFontWeight,
    fontFamily: 'inherit', cursor: 'pointer',
    flexShrink: 0, marginTop: '12px',
  }

  /* ── Grouped file list: one connected card, inset dividers ── */
  const renderGroupedFiles = (onPick) => (
    <div style={{
      background: MODAL_CONFIG.groupBg,
      borderRadius: MODAL_CONFIG.groupRadius,
      overflow: 'hidden',
    }}>
      {files.map((file, idx) => {
        const langInfo = getLanguageInfo(file.language)
        const displayName = file.name || `${CODE_LOG_CONFIG.defaultFileNamePrefix} ${idx + 1}`
        return (
          <button
            key={idx}
            onClick={() => onPick(idx)}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: `${MODAL_CONFIG.rowPaddingY} ${MODAL_CONFIG.rowPaddingX}`,
              color: MODAL_CONFIG.rowColor,
              fontSize: MODAL_CONFIG.rowFontSize,
              fontWeight: MODAL_CONFIG.rowFontWeight,
              fontFamily: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              position: 'relative',
              boxSizing: 'border-box',
            }}
          >
            {/* inset divider above each row except the first */}
            {idx !== 0 && (
              <span style={{
                position: 'absolute', top: 0,
                left: MODAL_CONFIG.dividerInset, right: MODAL_CONFIG.dividerInset,
                height: '0.5px', background: MODAL_CONFIG.dividerColor,
              }} />
            )}
            <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {displayName}
            </span>
            {langInfo && (
              <span style={{ fontSize: '15px', fontWeight: 400, color: '#2f93ff', flexShrink: 0 }}>
                {langInfo.label}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )

  /* ── The bottom sheet (shared by create + edit; differs only by
   *    whether the X/checkmark top row shows) ── */
  const renderSheet = () => (
    <div
      onClick={() => { if (isEdit) { /* edit: backdrop does nothing, must use chevron */ } else { onClose() } }}
      style={{
        position: 'fixed', left: 0, right: 0, top: 0, bottom: 0,
        height: '100dvh',
        zIndex: 100002,
        background: 'transparent',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: MODAL_CONFIG.sheetBg,
          width: '100%', maxWidth: MODAL_CONFIG.sheetWidth,
          height: `${MODAL_CONFIG.sheetHeightVh}vh`,
          maxHeight: `${MODAL_CONFIG.sheetHeightVh}dvh`,
          borderRadius: `${MODAL_CONFIG.sheetRadius} ${MODAL_CONFIG.sheetRadius} 0 0`,
          padding: `${MODAL_CONFIG.sheetPaddingTop} ${MODAL_CONFIG.sheetPaddingX} ${MODAL_CONFIG.sheetPaddingBottom}`,
          color: '#fff', display: 'flex', flexDirection: 'column',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          animation: 'codemodal-slide 0.24s cubic-bezier(.32,.72,0,1)',
          boxSizing: 'border-box',
          pointerEvents: 'auto',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
        }}
      >
        {/* Drag handle */}
        <div style={{
          width: MODAL_CONFIG.handleWidth, height: MODAL_CONFIG.handleHeight,
          background: MODAL_CONFIG.handleColor, borderRadius: '3px',
          margin: `0 auto ${MODAL_CONFIG.handleMarginBottom}`, flexShrink: 0,
        }} />

        {/* TOP ROW (create only): X + checkmark */}
        {!isEdit && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: MODAL_CONFIG.topRowMarginBottom, flexShrink: 0,
          }}>
            <Pressable onPress={onClose} style={topBtnStyle(MODAL_CONFIG.cancelBtnBg)} title="Cancel">
              <XMark size={13} />
            </Pressable>
            <Pressable onPress={handleConfirm} style={topBtnStyle(MODAL_CONFIG.confirmBtnBg)} title="Done">
              <CheckMark size={15} />
            </Pressable>
          </div>
        )}

        {/* SCROLLABLE CONTENT */}
        <div className="no-sb" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* TITLE (create only — edit renames via pad/title elsewhere) */}
          {!isEdit && (
            <div style={{ marginBottom: MODAL_CONFIG.sectionMarginBottom }}>
              <div style={{
                color: MODAL_CONFIG.sectionLabelColor,
                fontSize: MODAL_CONFIG.sectionLabelFontSize,
                fontWeight: MODAL_CONFIG.sectionLabelFontWeight,
                marginBottom: MODAL_CONFIG.sectionLabelMarginBottom,
              }}>
                Title
              </div>
              <input
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setError('') }}
                placeholder="Title"
                style={{
                  width: '100%',
                  background: MODAL_CONFIG.groupBg,
                  border: 'none',
                  borderRadius: MODAL_CONFIG.groupRadius,
                  padding: `${MODAL_CONFIG.rowPaddingY} ${MODAL_CONFIG.rowPaddingX}`,
                  color: MODAL_CONFIG.rowColor,
                  fontSize: MODAL_CONFIG.titleInputFontSize,
                  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          {/* FILES */}
          <div style={{ marginBottom: MODAL_CONFIG.sectionMarginBottom }}>
            <div style={{
              color: MODAL_CONFIG.sectionLabelColor,
              fontSize: MODAL_CONFIG.sectionLabelFontSize,
              fontWeight: MODAL_CONFIG.sectionLabelFontWeight,
              marginBottom: MODAL_CONFIG.sectionLabelMarginBottom,
            }}>
              Files
            </div>
            {renderGroupedFiles((idx) => {
              setOpenFileIdx(idx)
              setSheetVisible(false)  // enter the file's pad full-screen
            })}
          </div>

          {error && (
            <div style={{ color: '#FF453A', fontSize: '13px', marginBottom: '8px', textAlign: 'center' }}>
              {error}
            </div>
          )}
        </div>

        {/* ADD PILL */}
        <Pressable onPress={handleAddFileRow} style={addPillStyle} pressScale={0.97}>Add</Pressable>
      </div>
    </div>
  )

  /* ── RENDER ──
   *  Layer 1 (back): the pad, whenever a file is open.
   *  Layer 2 (front): the sheet, whenever sheetVisible.
   *  In EDIT, both can be true at once (pad behind, sheet in front).
   *  In CREATE, opening a file hides the sheet (full-screen pad);
   *  the pencil-square brings the sheet back over it. */
  const padOpen = openFileIdx !== null && files[openFileIdx]
  // In create mode, when a file is open we only show the sheet if it was
  // explicitly popped (pencil). Track that via sheetVisible.
  const showPad = padOpen
  const showSheet = sheetVisible

  return (
    <>
      {/* hidden browse input */}
      <input ref={browseInputRef} type="file" accept=".txt,.js,.jsx,.ts,.tsx,.py,.swift,.java,.c,.h,.cpp,.cs,.go,.rs,.php,.rb,.kt,.sh,.sql,.html,.css,.json,.md,.m,.r" onChange={handleBrowseSelected} style={{ display: 'none' }} />

      {/* Backdrop dim (only when no pad behind — pure create sheet) */}
      {showSheet && !showPad && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 100000,
            background: MODAL_CONFIG.overlayBg,
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            animation: 'codemodal-fade 0.18s ease-out',
          }}
        />
      )}

      {/* LAYER 1: pad behind */}
      {showPad && (
        <CodeFilePad
          file={files[openFileIdx]}
          fileIndex={openFileIdx}
          inBackground={showSheet}
          onChange={(next) => updateFileAt(openFileIdx, next)}
          onBack={() => { if (isEdit) { saveAndClose() } else { setOpenFileIdx(null); setSheetVisible(true) } }}
          onAddFile={handlePencilFromPad}
          onBrowse={() => handleBrowse(openFileIdx)}
          onCopy={() => handleCopy(openFileIdx)}
          onToggleStar={() => onToggleStar && onToggleStar()}
          isStarred={!!isStarred}
          onDeleteFile={() => handleDeleteFile(openFileIdx)}
        />
      )}

      {/* LAYER 2: sheet in front */}
      {showSheet && renderSheet()}

      <style>{`
        @keyframes codemodal-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes codemodal-slide { from { transform: translateY(100%) } to { transform: translateY(0) } }
      `}</style>
    </>
  )
}

export default CodeModal